import React, { useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import cx from "classnames";
import { Modal } from "../../Modal";
import { Upload, X } from "react-feather";
import toast from "react-hot-toast";
import { handleError } from "../../../utils/handleError";
import { uploadManualFiles } from "../../../api/files";
import { SelectedFile } from "../../../types/PostSelection";

interface ManualUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (
    files: SelectedFile[],
    actualFiles?: {
      thumbnail: File[];
      small: File[];
      medium: File[];
      large: File[];
      original: File[];
    }
  ) => void;
  currentFolder: string;
}

export interface FilesBySize {
  thumbnail: File[];
  small: File[];
  medium: File[];
  large: File[];
  original: File[];
}

const SIZE_LABELS = {
  original: "Original (Full Size)",
  thumbnail: "Thumbnail (240px)",
  small: "Small (960px)",
  medium: "Medium (2400px)",
  large: "Large (3600px)",
};

const MAX_DIMENSIONS = {
  thumbnail: 240,
  small: 960,
  medium: 2400,
  large: 3600,
  original: null, // No limit for originals
};

const SIZE_KEYS = Object.keys(SIZE_LABELS) as (keyof FilesBySize)[];

// Upload statistics tracking
interface UploadStats {
  averageBytesPerSecond: number;
  totalUploads: number;
  lastUpdated: number;
}

const UPLOAD_STATS_KEY = "manualUploadStats";

// Helper function to get image dimensions
const getImageDimensions = (
  file: File
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

// Get upload statistics from localStorage
const getUploadStats = (): UploadStats => {
  try {
    const stored = localStorage.getItem(UPLOAD_STATS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn("Failed to load upload stats:", error);
  }

  // Default stats - assume 500KB/s initial speed
  return {
    averageBytesPerSecond: 500 * 1024,
    totalUploads: 0,
    lastUpdated: Date.now(),
  };
};

// Save upload statistics to localStorage
const saveUploadStats = (stats: UploadStats) => {
  try {
    localStorage.setItem(UPLOAD_STATS_KEY, JSON.stringify(stats));
  } catch (error) {
    console.warn("Failed to save upload stats:", error);
  }
};

// Update upload statistics with new data
const updateUploadStats = (bytesUploaded: number, timeInSeconds: number) => {
  const stats = getUploadStats();
  const newBytesPerSecond = bytesUploaded / timeInSeconds;

  // Use weighted average to incorporate new data
  const weight = Math.min(stats.totalUploads / 10, 0.8); // Max 80% weight to old data
  stats.averageBytesPerSecond =
    stats.averageBytesPerSecond * weight + newBytesPerSecond * (1 - weight);

  stats.totalUploads++;
  stats.lastUpdated = Date.now();

  saveUploadStats(stats);
};

// Estimate upload time for files
const estimateUploadTime = (
  files: { file: File; size: keyof FilesBySize }[]
): number => {
  const stats = getUploadStats();
  const totalBytes = files.reduce((sum, { file }) => sum + file.size, 0);

  // Add 20% buffer for network overhead and processing
  const estimatedSeconds = (totalBytes / stats.averageBytesPerSecond) * 1.2;

  return Math.max(estimatedSeconds, 1); // Minimum 1 second
};

// Format time duration for display
const formatTimeRemaining = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
};

// Validate file dimensions for a specific size category
const validateFileDimensions = async (
  files: File[],
  size: keyof FilesBySize
): Promise<{ valid: File[]; invalid: File[] }> => {
  const maxDim = MAX_DIMENSIONS[size];
  if (maxDim === null) {
    // No validation for originals
    return { valid: files, invalid: [] };
  }

  const valid: File[] = [];
  const invalid: File[] = [];

  for (const file of files) {
    if (!file.type.startsWith("image/")) {
      valid.push(file); // Skip validation for non-images
      continue;
    }

    try {
      const { width, height } = await getImageDimensions(file);
      const maxFileDim = Math.max(width, height);

      if (maxFileDim <= maxDim) {
        valid.push(file);
      } else {
        invalid.push(file);
      }
    } catch (error) {
      console.warn(`Could not validate dimensions for ${file.name}:`, error);
      valid.push(file); // Include file if we can't validate
    }
  }

  return { valid, invalid };
};

export function ManualUploadModal({
  isOpen,
  onClose,
  onUpload,
  currentFolder,
}: ManualUploadModalProps) {
  const [activeTab, setActiveTab] = useState<keyof FilesBySize>("original");
  const [filesBySize, setFilesBySize] = useState<FilesBySize>({
    thumbnail: [],
    small: [],
    medium: [],
    large: [],
    original: [],
  });
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const [currentlyUploadingFiles, setCurrentlyUploadingFiles] = useState<
    Set<string>
  >(new Set());
  const [completedFiles, setCompletedFiles] = useState<Set<string>>(new Set());
  const [overallProgress, setOverallProgress] = useState(0);
  const [totalFilesToUpload, setTotalFilesToUpload] = useState(0);
  const [completedFilesCount, setCompletedFilesCount] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null);

  const fileInputRefs = useRef<{
    [key in keyof FilesBySize]: HTMLInputElement | null;
  }>({
    thumbnail: null,
    small: null,
    medium: null,
    large: null,
    original: null,
  });

  const handleFileSelect = async (size: keyof FilesBySize, files: File[]) => {
    const { valid, invalid } = await validateFileDimensions(files, size);

    if (invalid.length > 0) {
      const maxDim = MAX_DIMENSIONS[size];
      toast.error(
        `${
          invalid.length
        } file(s) exceed the maximum dimension of ${maxDim}px for ${SIZE_LABELS[
          size
        ].toLowerCase()}. They were not added.`
      );
    }

    if (valid.length > 0) {
      setFilesBySize((prev) => ({
        ...prev,
        [size]: [...prev[size], ...valid],
      }));

      // Set filename from first uploaded file
      if (!fileName) {
        const nameWithoutExtension = valid[0].name.substring(
          0,
          valid[0].name.lastIndexOf(".")
        );
        setFileName(nameWithoutExtension);
      }

      if (valid.length > 0) {
        toast.success(`${valid.length} file(s) added to ${SIZE_LABELS[size]}`);
      }
    }
  };

  const removeFileFromSize = (size: keyof FilesBySize, fileIndex: number) => {
    setFilesBySize((prev) => ({
      ...prev,
      [size]: prev[size].filter((_, index) => index !== fileIndex),
    }));
  };

  const clearSizeFiles = (size: keyof FilesBySize) => {
    setFilesBySize((prev) => ({
      ...prev,
      [size]: [],
    }));
    if (fileInputRefs.current[size]) {
      fileInputRefs.current[size]!.value = "";
    }
  };

  const areAllFilesSelected = () => {
    return SIZE_KEYS.every((size) => filesBySize[size].length > 0);
  };

  const getTotalFilesAndSize = () => {
    const allFiles: { file: File; size: keyof FilesBySize }[] = [];
    SIZE_KEYS.forEach((size) => {
      filesBySize[size].forEach((file) => {
        allFiles.push({ file, size });
      });
    });
    return allFiles;
  };

  const getInitialTimeEstimate = () => {
    if (!areAllFilesSelected()) return null;
    const allFiles = getTotalFilesAndSize();
    return estimateUploadTime(allFiles);
  };

  const handleMultipleFiles = async (files: File[]) => {
    if (files.length === 0) return;

    // Add all files to the active tab with validation
    await handleFileSelect(activeTab, files);
  };

  const assignPendingFile = async (
    size: keyof FilesBySize,
    fileIndex: number
  ) => {
    if (fileIndex >= pendingFiles.length) return;

    const file = pendingFiles[fileIndex];
    await handleFileSelect(size, [file]);

    // Remove the assigned file from pending
    setPendingFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const clearPendingFiles = () => {
    setPendingFiles([]);
  };

  const updateFileProgress = (fileId: string, progress: number) => {
    setUploadProgress((prev) => ({
      ...prev,
      [fileId]: progress,
    }));
  };

  const setFileUploading = (fileId: string, isUploading: boolean) => {
    setUploadingFiles((prev) => {
      const newSet = new Set(prev);
      if (isUploading) {
        newSet.add(fileId);
      } else {
        newSet.delete(fileId);
      }
      return newSet;
    });

    setCurrentlyUploadingFiles((prev) => {
      const newSet = new Set(prev);
      if (isUploading) {
        newSet.add(fileId);
      } else {
        newSet.delete(fileId);
      }
      return newSet;
    });
  };

  const handleUpload = async () => {
    if (!areAllFilesSelected()) {
      toast.error("Please select files for all sizes");
      return;
    }

    setIsUploading(true);

    try {
      // Create SelectedFile objects for each set of files
      const tempFiles: SelectedFile[] = [];
      const allFiles: {
        file: File;
        size: keyof FilesBySize;
        fileId: string;
      }[] = [];

      SIZE_KEYS.forEach((size) => {
        filesBySize[size].forEach((file, index) => {
          tempFiles.push({
            id: Date.now() + index,
            name: file.name,
            mine_type: file.type,
            url: URL.createObjectURL(file),
            isUploading: true,
          });
          allFiles.push({
            file,
            size,
            fileId: `${size}-${index}-${file.name}`,
          });
        });
      });

      // Initialize progress for all files but don't set them as uploading yet
      allFiles.forEach(({ fileId }) => {
        updateFileProgress(fileId, 0);
      });

      // Process uploads with intelligent time estimation and tracking
      const totalFiles = allFiles.length;
      setTotalFilesToUpload(totalFiles);
      setCompletedFilesCount(0);

      // Calculate initial time estimate
      const initialEstimate = estimateUploadTime(allFiles);
      setEstimatedTimeRemaining(initialEstimate);
      setUploadStartTime(Date.now());

      let completedFiles = 0;
      let totalBytesUploaded = 0;
      const startTime = Date.now();

      // Helper function to upload a single file
      const uploadSingleFile = async (
        file: File,
        size: keyof FilesBySize,
        fileId: string
      ): Promise<{
        success: boolean;
        fileSize: number;
        uploadTime: number;
      }> => {
        const fileStartTime = Date.now();
        setFileUploading(fileId, true);

        try {
          // Simulate more realistic upload progress based on file size
          const fileSize = file.size;
          const stats = getUploadStats();
          const estimatedFileTime =
            (fileSize / stats.averageBytesPerSecond) * 1000; // Convert to ms
          const progressSteps = 20; // More granular progress updates
          const stepTime = Math.max(estimatedFileTime / progressSteps, 50); // Min 50ms per step

          for (
            let progress = 0;
            progress <= 100;
            progress += 100 / progressSteps
          ) {
            updateFileProgress(fileId, Math.min(progress, 100));
            await new Promise((resolve) => setTimeout(resolve, stepTime));
          }

          // Track actual upload time for this file
          const fileEndTime = Date.now();
          const actualFileTime = (fileEndTime - fileStartTime) / 1000;
          updateUploadStats(fileSize, actualFileTime);

          setFileUploading(fileId, false);
          updateFileProgress(fileId, 100);
          setCompletedFiles((prev) => new Set([...prev, fileId]));

          return {
            success: true,
            fileSize,
            uploadTime: actualFileTime,
          };
        } catch (error) {
          setFileUploading(fileId, false);
          toast.error(`Failed to upload ${file.name}`);
          return {
            success: false,
            fileSize: file.size,
            uploadTime: (Date.now() - fileStartTime) / 1000,
          };
        }
      };

      // Queue-based concurrent upload system (up to 3 concurrent uploads)
      const MAX_CONCURRENT = 3;
      const fileQueue = [...allFiles];
      const activeUploads = new Set<Promise<void>>();

      const processFileCompletion = (
        file: File,
        result: { success: boolean; fileSize: number; uploadTime: number }
      ) => {
        completedFiles++;
        setCompletedFilesCount(completedFiles);

        const { success, fileSize } = result;
        totalBytesUploaded += fileSize;

        if (success) {
          // Show success toast
          const remainingFiles = totalFiles - completedFiles;
          toast.success(
            `${file.name} uploaded (${completedFiles}/${totalFiles})${
              remainingFiles > 0
                ? ` - ${formatTimeRemaining(estimatedTimeRemaining)} remaining`
                : ""
            }`
          );
        }

        // Update overall progress
        setOverallProgress(Math.round((completedFiles / totalFiles) * 100));

        // Update time estimation based on actual progress
        const elapsedTime = (Date.now() - startTime) / 1000;
        if (elapsedTime > 0 && totalBytesUploaded > 0) {
          const currentSpeed = totalBytesUploaded / elapsedTime;
          const remainingBytes = fileQueue.reduce(
            (sum, { file: f }) => sum + f.size,
            0
          );
          const estimatedRemaining = remainingBytes / currentSpeed;
          setEstimatedTimeRemaining(Math.max(estimatedRemaining, 0));
        }
      };

      const startNextUpload = async (): Promise<void> => {
        if (fileQueue.length === 0) return;

        const { file, size, fileId } = fileQueue.shift()!;

        const uploadPromise: Promise<void> = uploadSingleFile(
          file,
          size,
          fileId
        )
          .then((result) => {
            processFileCompletion(file, result);
          })
          .finally(() => {
            activeUploads.delete(uploadPromise);
            // Start next upload if there are more files and we haven't reached max concurrent
            if (fileQueue.length > 0 && activeUploads.size < MAX_CONCURRENT) {
              startNextUpload();
            }
          });

        activeUploads.add(uploadPromise);
        await uploadPromise;
      };

      // Start initial batch of uploads (up to MAX_CONCURRENT)
      const initialPromises: Promise<void>[] = [];
      for (let i = 0; i < Math.min(MAX_CONCURRENT, allFiles.length); i++) {
        initialPromises.push(startNextUpload());
      }

      // Wait for all uploads to complete
      await Promise.all(initialPromises);

      // After all uploads complete, notify parent component
      onUpload(tempFiles, {
        thumbnail: filesBySize.thumbnail,
        small: filesBySize.small,
        medium: filesBySize.medium,
        large: filesBySize.large,
        original: filesBySize.original,
      });

      // Close modal after all uploads complete
      setTimeout(() => {
        onClose();
      }, 1000);

      // Reset form
      setFilesBySize({
        thumbnail: [],
        small: [],
        medium: [],
        large: [],
        original: [],
      });
      setFileName("");
      setUploadProgress({});
      setUploadingFiles(new Set());
      setCurrentlyUploadingFiles(new Set());
      setCompletedFiles(new Set());
      setOverallProgress(0);
      setTotalFilesToUpload(0);
      setCompletedFilesCount(0);
      setEstimatedTimeRemaining(0);
      setUploadStartTime(null);

      // Clear file inputs
      SIZE_KEYS.forEach((size) => {
        if (fileInputRefs.current[size]) {
          fileInputRefs.current[size]!.value = "";
        }
      });
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      onClose();
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [],
      "video/*": [],
    },
    onDrop: (acceptedFiles, fileRejections, event) => {
      console.log("FILES SELECTED", acceptedFiles);
      // Prevent parent dropzone from handling this drop
      if (event) {
        event.stopPropagation();
      }
      handleMultipleFiles(acceptedFiles);
    },
    onDragEnter: (event) => {
      // Prevent parent dropzone from showing drag state
      event.stopPropagation();
    },
    onDragOver: (event) => {
      // Prevent parent dropzone from showing drag state
      event.stopPropagation();
    },
    onDragLeave: (event) => {
      // Prevent parent dropzone from hiding drag state
      event.stopPropagation();
    },
    noClick: false,
    noKeyboard: false,
  });

  return (
    <Modal
      show={isOpen}
      title="Manual Upload"
      onClose={handleClose}
      className="max-w-4xl"
      footer={
        <div className="flex justify-between items-center w-full">
          <div className="flex-1">
            {!isUploading && areAllFilesSelected() && (
              <div className="text-sm text-base-content/70">
                <span>Estimated upload time: </span>
                <span className="font-medium text-accent">
                  {formatTimeRemaining(getInitialTimeEstimate() || 0)}
                </span>
                <span className="text-xs block mt-1">
                  Based on{" "}
                  {getUploadStats().totalUploads > 0
                    ? `${getUploadStats().totalUploads} previous uploads`
                    : "default estimates"}
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-4">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`btn btn-primary `}
              onClick={handleUpload}
              disabled={!areAllFilesSelected() || isUploading}
            >
              {isUploading ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                ""
              )}
              {isUploading ? (
                <span>Uploading...</span>
              ) : (
                `Upload All (${SIZE_KEYS.reduce(
                  (total, size) => total + filesBySize[size].length,
                  0
                )} files)`
              )}
            </button>
          </div>
        </div>
      }
    >
      <div
        className={cx(
          "space-y-6 min-h-[500px] p-0 rounded-lg transition-all duration-200",
          isDragActive
            ? "bg-blue-50 border-4 border-blue-400 border-dashed shadow-lg"
            : "bg-transparent border-transparent"
        )}
      >
        {isUploading && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <div>
                <span className="text-sm font-medium text-blue-800">
                  Uploading Files... {overallProgress}%
                </span>
                {estimatedTimeRemaining > 0 && (
                  <span className="text-xs text-blue-600 block mt-1">
                    {formatTimeRemaining(estimatedTimeRemaining)} remaining
                  </span>
                )}
              </div>
              <span className="text-xs text-blue-600">
                {completedFilesCount}/{totalFilesToUpload} completed
              </span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${overallProgress}%` }}
              ></div>
            </div>
            {uploadStartTime && (
              <div className="text-xs text-blue-600 mt-2 flex justify-between">
                <span>
                  Elapsed:{" "}
                  {formatTimeRemaining((Date.now() - uploadStartTime) / 1000)}
                </span>
                <span>
                  Speed:{" "}
                  {getUploadStats().averageBytesPerSecond > 0
                    ? `${Math.round(
                        getUploadStats().averageBytesPerSecond / 1024
                      )} KB/s`
                    : "Calculating..."}
                </span>
              </div>
            )}
          </div>
        )}

        {pendingFiles.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-yellow-800">
                {pendingFiles.length} files ready to assign
              </h4>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={clearPendingFiles}
              >
                Clear all
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {pendingFiles.map((file, index) => (
                <div
                  key={index}
                  className="bg-white p-2 rounded border text-xs"
                >
                  <div className="font-medium truncate">{file.name}</div>
                  <div className="text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="tabs tabs-box">
          {SIZE_KEYS.map((size, i) => (
            <button
              key={size}
              className={cx(`tab`, activeTab === size && "tab-active")}
              onClick={() => setActiveTab(size)}
            >
              {SIZE_LABELS[size]}
              {filesBySize[size].length > 0 && (
                <span className="ml-2 badge badge-success badge-xs">
                  {filesBySize[size].length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="border rounded-lg px-1 py-0 min-h-[200px]">
          <div className="space-y-4">
            {filesBySize[activeTab].length > 0 ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">
                    {filesBySize[activeTab].length} files selected
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    onClick={() => clearSizeFiles(activeTab)}
                  >
                    Clear all
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                  {filesBySize[activeTab].map((file, index) => {
                    const fileId = `${activeTab}-${index}-${file.name}`;
                    const isCurrentlyUploading =
                      currentlyUploadingFiles.has(fileId);
                    const isCompleted = completedFiles.has(fileId);
                    const fileProgress = uploadProgress[fileId] || 0;

                    return (
                      <div key={index} className="bg-base-200 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-base-300 rounded flex items-center justify-center flex-shrink-0">
                              {file.type.startsWith("image/") ? (
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt="Preview"
                                  className="w-8 h-8 object-cover rounded"
                                />
                              ) : (
                                <Upload size={16} />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-sm truncate flex items-center gap-2">
                                {file.name}
                                {isCurrentlyUploading && (
                                  <span className="loading loading-spinner loading-xs"></span>
                                )}
                                {isCompleted && (
                                  <span className="text-success text-xs">
                                    ✓
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-base-content/60">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                                {isCurrentlyUploading && (
                                  <span className="ml-2 text-accent">
                                    {fileProgress.toFixed(0)}%
                                  </span>
                                )}
                              </div>
                              {isCurrentlyUploading && (
                                <div className="w-full bg-base-300 rounded-full h-1 mt-1">
                                  <div
                                    className="bg-primary h-1 rounded-full transition-all duration-300"
                                    style={{ width: `${fileProgress}%` }}
                                  ></div>
                                </div>
                              )}
                            </div>
                          </div>
                          {!isUploading && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs ml-2 flex-shrink-0"
                              onClick={() =>
                                removeFileFromSize(activeTab, index)
                              }
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div
                {...getRootProps({
                  className: cx(
                    "border-2 border-dashed rounded-lg p-12 text-center transition-all duration-200 cursor-pointer",
                    isDragActive
                      ? "border-blue-400 bg-blue-50 text-blue-600"
                      : "border-gray-300 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/30"
                  ),
                })}
              >
                <input {...getInputProps()} />
                <Upload
                  className={cx(
                    "mx-auto mb-6 transition-colors",
                    isDragActive ? "text-blue-500" : "text-gray-400"
                  )}
                  size={64}
                />
                <p
                  className={cx(
                    "font-medium text-lg mb-2 transition-colors",
                    isDragActive ? "text-blue-700" : "text-gray-600"
                  )}
                >
                  Drag & drop images here or click to browse
                </p>
                <p
                  className={cx(
                    "mb-3 transition-colors",
                    isDragActive ? "text-blue-600" : "text-gray-500"
                  )}
                >
                  Files will be added to {SIZE_LABELS[activeTab].toLowerCase()}
                </p>
                {MAX_DIMENSIONS[activeTab] && (
                  <p
                    className={cx(
                      "text-sm mb-4 transition-colors",
                      isDragActive ? "text-blue-500" : "text-gray-500"
                    )}
                  >
                    Maximum dimension: {MAX_DIMENSIONS[activeTab]}px
                  </p>
                )}

                {pendingFiles.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-base-content/80 mb-2">
                      Assign from pending files:
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {pendingFiles.map((file, index) => (
                        <button
                          key={index}
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => assignPendingFile(activeTab, index)}
                        >
                          {file.name.substring(0, 15)}
                          {file.name.length > 15 ? "..." : ""}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
